import type React from 'react';
import { useState } from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { checkIsPaidSiteProductKey, getSiteProductDefinition } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Card,
    CardHighlight,
    HoverCard,
    HoverCardRoot,
    HoverCardTrigger,
} from '@gitbook/spine-react';

import { useNavigationState } from '../../navigation';
import { getUpgradeScreenNavigationTarget, useTrial } from '../../state';
import { createDocumentationURL } from '../../utils';
import { getTrialEndingNotice } from '../OrganizationBilling';

interface SiteTypeBadgeProps {
    site: api.Site;
    organization: api.Organization;
    children: React.ReactElement;
}

export function SiteTypeHoverCard(props: SiteTypeBadgeProps) {
    const { site, organization, children } = props;
    const definition = getSiteProductDefinition(site.type);
    const trial = useTrial({ organization });
    const { screen } = useNavigationState();
    const hasOngoingTrial =
        trial.status === api.BillingTrialStatus.Active ||
        trial.status === api.BillingTrialStatus.Expired;
    const isPaidPlan = checkIsPaidSiteProductKey(site.type);
    const [isOpen, setIsOpen] = useState(false);
    return (
        <HoverCardRoot isOpen={isOpen} onOpenChange={setIsOpen}>
            <HoverCardTrigger>{children}</HoverCardTrigger>
            <HoverCard>
                <Card className="relative max-w-96 overflow-hidden">
                    <CardHighlight
                        kind={isPaidPlan ? 'branded' : 'neutral'}
                        className="flex items-center justify-between gap-4 px-3 py-2"
                    >
                        <h3 className="text-ui-heading-small">{definition.title} Site</h3>
                        {isPaidPlan && hasOngoingTrial ? (
                            <Badge
                                kind="upgrade"
                                label={getTrialEndingNotice({
                                    daysLeft: trial.daysLeft,
                                    prefix: 'GitBook',
                                })}
                            />
                        ) : null}
                    </CardHighlight>
                    <div className="flex flex-col gap-3 p-3">
                        <p>{definition.description}</p>
                        <div className="flex flex-wrap items-center justify-end gap-3">
                            <Button
                                kind="blank"
                                size="small"
                                label="Learn more"
                                href={createDocumentationURL('account-management/plans')}
                                target="_blank"
                                trailingIcon={Icon.ExternalLink}
                            />
                            {isPaidPlan && hasOngoingTrial && organization.permissions.admin ? (
                                <Button
                                    label="Subscribe"
                                    kind="upgrade"
                                    size="small"
                                    href={getUpgradeScreenNavigationTarget({
                                        organizationId: organization.id,
                                        reason: 'site_type_hovercard',
                                        returnScreen: screen,
                                    })}
                                    onPress={() => {
                                        setIsOpen(false);
                                        analytics.logEvent(
                                            analytics.createOrgEvent(organization.id, {
                                                type: 'organization_open_upgrade',
                                                currentFeaturesSet: organization.plan,
                                                reason: 'site_type_hovercard',
                                            })
                                        );
                                    }}
                                />
                            ) : trial.status !== api.BillingTrialStatus.None &&
                              organization.permissions.admin ? (
                                <Button
                                    kind={site.type === api.SiteType.Basic ? 'upgrade' : 'primary'}
                                    label={
                                        site.type === api.SiteType.Basic
                                            ? 'Upgrade'
                                            : 'Manage site plan'
                                    }
                                    size="small"
                                    onPress={() => {
                                        setIsOpen(false);
                                    }}
                                    href={{
                                        screen: {
                                            type: 'site',
                                            site: site.id,
                                            organization: organization.id,
                                            tab: {
                                                type: 'settings',
                                                section: 'plan',
                                            },
                                        },
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </Card>
            </HoverCard>
        </HoverCardRoot>
    );
}
